import React, { useState, useEffect } from 'react';
import { post } from '../../../services/smartApiService';
import { AI_TRADER_URLS } from '../../../api/AiTraderUrls';

interface Company {
  symbol: string;
  name: string;
  weight: number;
  trend: 'bullish' | 'bearish' | 'neutral';
  rsi?: number;
  change_pct?: number;
  current_price?: number;
  macd_signal?: string;
  sma_signal?: string;
  latest_pattern?: string;
  pattern_signal?: string;
}

interface WeightMapData {
  symbol: string;
  companies: Company[];
  summary: {
    total_weight: number;
    bullish_weight_pct: number;
    bearish_weight_pct: number;
  };
}

interface Props {
  niftyData: WeightMapData | null;
  bankniftyData: WeightMapData | null;
  loading: boolean;
  onRefresh: () => void;
}

const TC = { bullish: '#26a65b', bearish: '#e74c3c', neutral: '#f39c12' };
const TI = { bullish: '▲', bearish: '▼', neutral: '—' };

function SummaryBar({ summary }: { summary: WeightMapData['summary'] }) {
  const bull = summary?.bullish_weight_pct || 0;
  const bear = summary?.bearish_weight_pct || 0;
  const neut = Math.max(0, 100 - bull - bear);
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
        <span style={{ color: '#26a65b', fontSize: '0.65rem', fontWeight: 700 }}>▲ Bull {bull.toFixed(1)}%</span>
        <span style={{ color: '#f39c12', fontSize: '0.65rem' }}>— Neutral {neut.toFixed(1)}%</span>
        <span style={{ color: '#e74c3c', fontSize: '0.65rem', fontWeight: 700 }}>▼ Bear {bear.toFixed(1)}%</span>
      </div>
      <div style={{ height: 6, borderRadius: 3, display: 'flex', overflow: 'hidden' }}>
        <div style={{ width: `${bull}%`, background: '#26a65b', transition: 'width 0.3s' }} />
        <div style={{ width: `${neut}%`, background: '#f39c1244' }} />
        <div style={{ width: `${bear}%`, background: '#e74c3c', transition: 'width 0.3s' }} />
      </div>
    </div>
  );
}

function CompanyTable({ data, isMobile }: { data: WeightMapData; isMobile: boolean }) {
  const [showAll, setShowAll] = useState(false);
  const maxWeight = Math.max(...data.companies.map(c => c.weight), 1);
  const list = showAll ? data.companies : data.companies.slice(0, 20);

  const cols = isMobile
    ? '58px 1fr 62px 44px 34px 42px'
    : '68px 1fr 72px 50px 38px 38px 80px 46px';
  const headers = isMobile
    ? ['SYMBOL', 'WEIGHT', 'PRICE', 'CHG%', 'RSI', 'TREND']
    : ['SYMBOL', 'WEIGHT', 'PRICE', 'CHG%', 'RSI', 'MACD', 'PATTERN', 'TREND'];

  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: cols,
        padding: '4px 6px',
        background: '#111',
        borderRadius: '4px 4px 0 0',
        marginBottom: 1,
      }}>
        {headers.map(h => (
          <div key={h} style={{ color: '#444', fontSize: '0.58rem', fontWeight: 700, textAlign: h === 'SYMBOL' || h === 'WEIGHT' ? 'left' : 'center' }}>{h}</div>
        ))}
      </div>

      <div style={{ maxHeight: 360, overflowY: 'auto', scrollbarWidth: 'thin' }}>
        {list.map((c, idx) => {
          const tColor = TC[c.trend] || '#888';
          const barW = Math.round((c.weight / maxWeight) * 100);
          const rsiColor = !c.rsi ? '#555' : c.rsi > 65 ? '#e74c3c' : c.rsi < 40 ? '#26a65b' : '#aaa';
          const macdColor = c.macd_signal === 'bullish' ? '#26a65b' : c.macd_signal === 'bearish' ? '#e74c3c' : '#555';
          const chgColor = c.change_pct == null ? '#555' : c.change_pct >= 0 ? '#26a65b' : '#e74c3c';
          const patColor = c.pattern_signal === 'bullish' ? '#26a65b' : c.pattern_signal === 'bearish' ? '#e74c3c' : '#555';
          return (
            <div key={c.symbol} style={{
              display: 'grid',
              gridTemplateColumns: cols,
              alignItems: 'center',
              padding: '5px 6px',
              background: idx % 2 === 0 ? '#0d0d14' : '#0a0a10',
              borderBottom: '1px solid #0f0f18',
            }}>
              {/* Symbol */}
              <div>
                <div style={{ color: '#ddd', fontSize: '0.68rem', fontWeight: 700 }}>{c.symbol}</div>
                {c.name && !isMobile && (
                  <div style={{ color: '#444', fontSize: '0.55rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 64 }}>
                    {c.name.split(' ')[0]}
                  </div>
                )}
              </div>

              {/* Weight bar */}
              <div style={{ paddingRight: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <div style={{ flex: 1, height: 5, background: '#1a1a2a', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ width: `${barW}%`, height: '100%', background: tColor, borderRadius: 3, transition: 'width 0.3s' }} />
                  </div>
                  <span style={{ color: '#666', fontSize: '0.58rem', flexShrink: 0 }}>{c.weight.toFixed(1)}%</span>
                </div>
              </div>

              {/* Price */}
              <div style={{ color: '#bbb', fontSize: '0.65rem', fontWeight: 600, textAlign: 'center' }}>
                {c.current_price ? c.current_price.toLocaleString('en-IN', { maximumFractionDigits: 0 }) : '—'}
              </div>

              {/* Change % */}
              <div style={{ color: chgColor, fontSize: '0.65rem', fontWeight: 700, textAlign: 'center' }}>
                {c.change_pct != null ? `${c.change_pct >= 0 ? '+' : ''}${c.change_pct.toFixed(1)}%` : '—'}
              </div>

              {/* RSI */}
              <div style={{ color: rsiColor, fontSize: '0.65rem', fontWeight: 700, textAlign: 'center' }}>
                {c.rsi ? c.rsi.toFixed(0) : '—'}
              </div>

              {/* MACD — desktop only */}
              {!isMobile && (
                <div style={{ color: macdColor, fontSize: '0.65rem', textAlign: 'center' }}>
                  {c.macd_signal === 'bullish' ? '▲' : c.macd_signal === 'bearish' ? '▼' : '—'}
                </div>
              )}

              {/* Pattern — desktop only */}
              {!isMobile && (
                <div style={{ textAlign: 'center' }}>
                  {c.latest_pattern ? (
                    <span style={{ color: patColor, fontSize: '0.58rem' }}
                      title={`${c.latest_pattern} (${c.pattern_signal || 'neutral'})`}>
                      {c.latest_pattern.replace(/_/g, ' ').split(' ').map((w: string) => w[0]).join('').slice(0, 4).toUpperCase()}
                    </span>
                  ) : (
                    <span style={{ color: '#333', fontSize: '0.58rem' }}>—</span>
                  )}
                </div>
              )}

              {/* Trend */}
              <div style={{ textAlign: 'center' }}>
                <span style={{
                  color: tColor,
                  background: `${tColor}18`,
                  border: `1px solid ${tColor}33`,
                  fontSize: '0.58rem',
                  fontWeight: 700,
                  padding: '1px 5px',
                  borderRadius: 8,
                }}>
                  {TI[c.trend]} {c.trend.charAt(0).toUpperCase() + c.trend.slice(1, 4)}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {data.companies.length > 20 && (
        <button onClick={() => setShowAll(s => !s)} style={{
          width: '100%', background: 'transparent',
          border: '1px solid #1a1a2a', color: '#555',
          fontSize: '0.62rem', padding: '5px', borderRadius: '0 0 4px 4px',
          cursor: 'pointer',
        }}>
          {showAll ? 'Show fewer' : `Show all ${data.companies.length} companies`}
        </button>
      )}
    </div>
  );
}

export default function IndexWeightMap({ niftyData, bankniftyData, loading, onRefresh }: Props) {
  const [activeTab, setActiveTab] = useState<'NIFTY' | 'BANKNIFTY'>('NIFTY');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [refreshing, setRefreshing] = useState(false);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  const data = activeTab === 'NIFTY' ? niftyData : bankniftyData;

  const handleRefresh = () => {
    setRefreshing(true);
    post(AI_TRADER_URLS.REFRESH_SNAPSHOTS, {}, { showLoading: false }).subscribe({
      next: () => { setTimeout(() => { setRefreshing(false); onRefresh(); }, 3000); },
      error: () => setRefreshing(false),
    });
  };

  if (loading) {
    return <div style={{ padding: '1rem', color: '#555', fontSize: '0.72rem', textAlign: 'center' }}>Loading companies...</div>;
  }

  return (
    <div>
      {/* Tabs + Refresh */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 10, alignItems: 'center' }}>
        {(['NIFTY', 'BANKNIFTY'] as const).map(sym => {
          const d = sym === 'NIFTY' ? niftyData : bankniftyData;
          const bull = d?.summary?.bullish_weight_pct || 0;
          const bear = d?.summary?.bearish_weight_pct || 0;
          const isActive = activeTab === sym;
          return (
            <button key={sym} onClick={() => setActiveTab(sym)} style={{
              padding: '4px 12px', borderRadius: 6,
              background: isActive ? '#0d1a2a' : 'transparent',
              border: `1px solid ${isActive ? '#4fc3f7' : '#1a1a2a'}`,
              color: isActive ? '#4fc3f7' : '#555',
              fontSize: '0.68rem', fontWeight: isActive ? 700 : 400, cursor: 'pointer',
            }}>
              {sym}
              {d?.companies?.length ? (
                <span style={{ marginLeft: 6, fontSize: '0.58rem', color: '#555' }}>
                  {d.companies.length} co
                </span>
              ) : null}
              {bull > 50 && <span style={{ marginLeft: 4, color: '#26a65b', fontSize: '0.58rem' }}>▲</span>}
              {bear > 50 && <span style={{ marginLeft: 4, color: '#e74c3c', fontSize: '0.58rem' }}>▼</span>}
            </button>
          );
        })}
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          style={{
            marginLeft: 4,
            background: 'transparent', border: '1px solid #1a2a1a',
            color: '#4caf50', fontSize: '0.6rem', padding: '2px 8px',
            borderRadius: 4, cursor: refreshing ? 'default' : 'pointer',
            opacity: refreshing ? 0.5 : 1,
          }}
        >
          {refreshing ? '⟳ Refreshing...' : '↺ Refresh'}
        </button>
        {!isMobile && (
          <div style={{ marginLeft: 'auto', color: '#444', fontSize: '0.6rem' }}>
            RSI: <span style={{ color: '#26a65b' }}>&lt;40=oversold</span> / <span style={{ color: '#e74c3c' }}>&gt;65=overbought</span>
          </div>
        )}
      </div>

      {!data?.companies?.length ? (
        <div style={{ padding: '0.75rem', color: '#555', fontSize: '0.72rem', textAlign: 'center', border: '1px solid #1a1a2a', borderRadius: 6 }}>
          No data for {activeTab}. Run <code style={{ color: '#4fc3f7' }}>seed_index_weights.py</code> to populate index components.
        </div>
      ) : (
        <>
          <SummaryBar summary={data.summary} />
          <CompanyTable data={data} isMobile={isMobile} />
        </>
      )}
    </div>
  );
}
